/** 报告链接识别：判断一段粘贴文本是否为「报告链接」。
 *  规则（与产品/后端约定）：
 *    1. 必须是 http(s):// 开头的链接；
 *    2. 路径或 hash 里含 `/report/<id>`，id 为 8 位及以上的 uuid 风格串（含短横线）。
 *  兼容 hash 路由（`.../#/zh/report/<uuid>?agentId=4`）——`/report/<id>` 可能落在 hash
 *  片段里，故直接对整条 url 做匹配，不依赖 URL.pathname/searchParams。 */
const REPORT_ID_RE = /\/report\/[0-9a-fA-F-]{8,}/;

export function isReportUrl(text: string): boolean {
  if (!text) return false;
  const trimmed = text.trim();
  if (!/^https?:\/\//i.test(trimmed)) return false;
  return REPORT_ID_RE.test(trimmed);
}

/** 只匹配 ASCII 合法 URL 字符——在中文/全角标点（如 `；`）处自然截断，
 *  故「链接；文案」这类粘贴能把 URL 与后续文案分开。 */
const REPORT_URL_RE = /https?:\/\/[A-Za-z0-9\-._~:/?#[\]@!$&'()*+,;=%]+/;

/** 从一段粘贴文本里切出「全部报告链接」和剩余文案。
 *  用于「粘贴报告链接 + 一段问题」的场景：链接进 footer 报告 chip，
 *  文案回落到输入框（见 handlePaste）。支持一次粘贴多个报告链接。
 *  - 找不到报告链接返回 null（调用方按普通文本处理）；
 *  - urls 按出现顺序去重（相同链接只算一个报告）；
 *  - rest 为移除所有报告链接后剩下的文案（非报告链接原样保留），去掉开头分隔符。 */
export function extractReportUrls(text: string): { urls: string[]; rest: string } | null {
  if (!text) return null;
  const re = new RegExp(REPORT_URL_RE.source, 'g');
  const urls: string[] = [];
  const seen = new Set<string>();
  let rest = '';
  let cursor = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    const token = m[0];
    if (!isReportUrl(token)) continue; // 非报告链接：不移除，原样留在文案里
    rest += text.slice(cursor, m.index);
    cursor = m.index + token.length;
    if (!seen.has(token)) {
      seen.add(token);
      urls.push(token);
    }
  }
  if (urls.length === 0) return null;
  rest += text.slice(cursor);
  rest = rest.replace(/^[\s；;，,、]+/, '').trim();
  return { urls, rest };
}
